// ./src/hooks/usePersonGroups.ts
//
// Lean hook for PersonGroups — enough to power the contact editor's group picker
// (list + lookup) and create-in-place (create). Not a full CRUD page hook.
import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { OP_RESULT_CODES, OpResult } from "@sdflc/api-helpers";

import {
  PersonGroup,
  PersonGroupService,
  PersonGroupFilter,
  PersonGroupInput,
  SortItem,
  listAllPages,
} from "@/utils/api";
import { upsertBy, removeBy } from "@/utils";
import { useApp } from "@/contexts";
import { useRefreshOnWriteTimeout } from "./useRefreshOnWriteTimeout";

export interface UsePersonGroupsOptions {
  autoLoad?: boolean;
}

export interface UsePersonGroupsReturn {
  personGroups: PersonGroup[];
  loading: boolean;
  isCreating: boolean;
  isUpdating: boolean;
  isRemoving: boolean;
  list: (filter?: Partial<PersonGroupFilter>) => Promise<OpResult>;
  create: (params: PersonGroupInput) => Promise<OpResult>;
  update: (id: string, params: PersonGroupInput) => Promise<OpResult>;
  remove: (id: string) => Promise<OpResult>;
  refresh: () => Promise<OpResult>;
  getById: (id: string | undefined) => PersonGroup | undefined;
  lastError: OpResult | null;
  clearError: () => void;
}

const DEFAULT_SORT: SortItem[] = [{ name: "name", order: "ASC" }];

export const usePersonGroups = (options: UsePersonGroupsOptions = {}): UsePersonGroupsReturn => {
  const { autoLoad = true } = options;

  const { user } = useApp();
  const userId = user?.id ?? null;
  const previousUserIdRef = useRef<string | null | undefined>(undefined);

  const [personGroups, setPersonGroups] = useState<PersonGroup[]>([]);
  /**
   * ⚠⚠ STARTS TRUE WHEN THIS HOOK IS GOING TO AUTO-LOAD — "about to read" is not "read, and empty".
   *
   * The auto-load effect is gated on `userId`, which arrives ASYNCHRONOUSLY from `AppContext`. So on
   * a hard reload or a first navigation there are renders where no request has started yet: with
   * `loading` false and the collection empty, every list component's
   * `!loading && items.length === 0` branch is TRUE and paints the first-run empty state before the
   * skeleton and then the data. Reported across the whole event space (owner, 2026-09-28).
   * ⓘ Fixed HERE rather than in the ~45 list components that read it: the lie is in the flag, not in
   * the branch, and a per-component fix would have to be remembered by every list written after it.
   * ⚠ `autoLoad: false` still starts FALSE — a hook told not to load must not claim to be loading.
   */
  const [loading, setLoading] = useState(autoLoad);
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [lastError, setLastError] = useState<OpResult | null>(null);

  const clearError = useCallback(() => {
    setLastError(null);
  }, []);

  const [hasListed, setHasListed] = useState(false);

  const list = useCallback(async (filter: Partial<PersonGroupFilter> = {}): Promise<OpResult> => {
    setHasListed(true);
    setLoading(true);
    setLastError(null);

    try {
      const result = await listAllPages(PersonGroupService.list, filter as PersonGroupFilter, DEFAULT_SORT);

      if (result.code === OP_RESULT_CODES.OK && result.data) {
        setPersonGroups(Array.isArray(result.data) ? (result.data as PersonGroup[]) : []);
        setLastError(null);

        return result;
      }

      setLastError(result);

      return result;
    } catch (error) {
      const result = OpResult.fromException(error);
      setLastError(result);

      return result;
    } finally {
      setLoading(false);
    }
  }, []);

  const create = useCallback(async (params: PersonGroupInput): Promise<OpResult> => {
    setIsCreating(true);
    setLastError(null);

    try {
      const result = await PersonGroupService.create(params);

      if (result.code === OP_RESULT_CODES.OK && result.data) {
        const created = (result.data as PersonGroup[])[0];
        setPersonGroups((prev) => upsertBy(prev, created));
        setLastError(null);

        return result;
      }

      setLastError(result);

      return result;
    } catch (error) {
      const result = OpResult.fromException(error);
      setLastError(result);

      return result;
    } finally {
      setIsCreating(false);
    }
  }, []);

  const update = useCallback(async (id: string, params: PersonGroupInput): Promise<OpResult> => {
    setIsUpdating(true);
    setLastError(null);

    try {
      const result = await PersonGroupService.update({ id }, params);

      if (result.code === OP_RESULT_CODES.OK && result.data) {
        const updated = (result.data as PersonGroup[])[0];
        setPersonGroups((prev) => upsertBy(prev, updated));
        setLastError(null);

        return result;
      }

      setLastError(result);

      return result;
    } catch (error) {
      const result = OpResult.fromException(error);
      setLastError(result);

      return result;
    } finally {
      setIsUpdating(false);
    }
  }, []);

  const remove = useCallback(async (id: string): Promise<OpResult> => {
    setIsRemoving(true);
    setLastError(null);

    try {
      const result = await PersonGroupService.remove(id);

      if (result.code === OP_RESULT_CODES.OK) {
        setPersonGroups((prev) => removeBy(prev, id));
        setLastError(null);

        return result;
      }

      setLastError(result);

      return result;
    } catch (error) {
      const result = OpResult.fromException(error);
      setLastError(result);

      return result;
    } finally {
      setIsRemoving(false);
    }
  }, []);

  const refresh = useCallback(async (): Promise<OpResult> => {
    return list({});
  }, [list]);

  // ROADMAP #131 — a save that timed out may still have landed: re-read what is shown — only once this hook has listed
  // (it keeps no loaded flag, and a form that only reads one record must not get an unrequested list read).
  useRefreshOnWriteTimeout(refresh, hasListed);

  const getById = useCallback(
    (id: string | undefined): PersonGroup | undefined => {
      if (!id) {
        return undefined;
      }

      return personGroups.find((item) => item.id === id);
    },
    [personGroups]
  );

  useEffect(() => {
    const previousUserId = previousUserIdRef.current;
    previousUserIdRef.current = userId;

    if (previousUserId === undefined) {
      if (autoLoad && userId) {
        list({});
      }

      return;
    }

    if (previousUserId !== userId && autoLoad && userId) {
      list({});
    }
  }, [userId, autoLoad, list]);

  return useMemo(
    () => ({
      personGroups,
      loading,
      isCreating,
      isUpdating,
      isRemoving,
      list,
      create,
      update,
      remove,
      refresh,
      getById,
      lastError,
      clearError,
    }),
    [
      personGroups,
      loading,
      isCreating,
      isUpdating,
      isRemoving,
      list,
      create,
      update,
      remove,
      refresh,
      getById,
      lastError,
      clearError,
    ]
  );
};
